import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { useSearch, useLocation, useNavigate } from '@tanstack/react-router'
import { useAuth } from '../../_shared/authService'
import { useBackendApiGetUser, useIsSignedInUserUnregistered } from '../../_shared/backend-api/user'
import { optimizeHtmlForLLM } from '../../../../_shared/html'
import { useStartAiChatSession } from './endpoints/startAiChatSession'
import { useSendMessage } from './endpoints/sendMessage'
import { useSubscribeAiChatSession } from './endpoints/subscribeAiChatSession'
import { useUpdateClientInfo } from './endpoints/updateClientInfo'
import { AiChatEventPayload, AiChatSession } from 'poc-ai-biz-irdb_domain-model'
import { useLoggingEffect } from '../../_shared/utils'
import { useStreamingAiChatEvents } from './endpoints/useStreamingAiChatEvents'
import { Result } from 'result-type-ts'
import { waitForPageUpdateComplete } from '../../../_shared/utils'
import { AiChatPageProps } from '../model'
import { toast } from 'sonner'

const awaitForSec = (sec: number): Promise<void> => {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve()
    }, sec * 1000)
  })
}

type ClientActionAiChatEventPayload = Extract<AiChatEventPayload, { type: 'clientAction' }>
type ClientInfoAiChatEventPayload = Extract<AiChatEventPayload, { type: 'clientInfo' }>

const isCompletedClientActionAiChatEventPayload = (payload: Partial<ClientActionAiChatEventPayload>): payload is ClientActionAiChatEventPayload => {
  return payload.type === 'clientAction' && typeof payload.actionType === 'string' && typeof payload.parameters === 'object' && payload.parameters !== null
}

// ページコンテキストを取得する関数
const getCurrentPageContext = (): ClientInfoAiChatEventPayload['pageContext'] => {
  const url = window.location.href

  // bodyのHTMLを取得してサニタイズ
  const bodyHtml = document.body.innerHTML
  const optimizedHtml = optimizeHtmlForLLM(bodyHtml)

  return {
    html: optimizedHtml,
    title: document.title,
    url,
  }
}

const getAvailableActionsByPathname = (
  pathname: string,
): {
  availableActions: ClientInfoAiChatEventPayload['availableActions']
  handler: (clientActionPayload: ClientActionAiChatEventPayload, navigate: ReturnType<typeof useNavigate>) => Result<void>
} => {
  // if (pathname.startsWith('/securities-report-chunks/')) {
  //   return {
  //     availableActions: securitiesReportChunkDetailPageAvailableActions,
  //     handler: securitiesReportChunkDetailPageAvailableActionHandlers,
  //   }
  // }
  // if (pathname === '/') {
  //   return {
  //     availableActions: companyUsecaseTablePageAvailableActions,
  //     handler: companyUsecaseTablePageAvailableActionHandlers,
  //   }
  // }
  return {
    availableActions: [],
    handler: () => Result.failure(new Error('No available actions for this page')),
  }
}

// ClientInfoを作成する関数
const createClientInfo = (pathname: string): ClientInfoAiChatEventPayload => {
  const pageContext = getCurrentPageContext()
  const { availableActions } = getAvailableActionsByPathname(pathname)

  return {
    type: 'clientInfo',
    pageContext,
    availableActions,
  }
}

export const useAiChatPage = (): AiChatPageProps => {
  const auth = useAuth()
  const useBackendApiGetUserResult = useBackendApiGetUser()
  const isSignedInUserUnregistered = useIsSignedInUserUnregistered()
  const location = useLocation()
  const navigate = useNavigate()
  const searchParams = useSearch({ from: '__root__' })

  // State
  const [aiChatSessionId, setAiChatSessionId] = useState<string | null>(null)
  const [currentAiChatSession, setCurrentAiChatSession] = useState<AiChatSession | null>(null)
  const [aiChatEventIds, setAiChatEventIds] = useState<string[]>([])
  const [isSendingMessage, setIsSendingMessage] = useState(false)
  const [isInitialized, setIsInitialized] = useState(false)

  // TextField ref
  const textFieldRef = useRef<HTMLInputElement>(null)

  const executedAiChatEventClientActionIds = useRef<Set<string>>(new Set())
  const abortControllerRef = useRef<AbortController | null>(null)
  const isSubscribedRef = useRef<boolean>(false)

  const startAiChatSession = useStartAiChatSession()
  const sendMessage = useSendMessage()
  const subscribeAiChatSession = useSubscribeAiChatSession()
  const updateClientInfo = useUpdateClientInfo()
  const aiChatEventsResult = useStreamingAiChatEvents(aiChatEventIds)

  // useBackendApiGetUserのエラーをtoast表示。未登録の利用者はログイン画面へ送るので、失敗として出さない
  useEffect(() => {
    if (auth.isAuthenticated && useBackendApiGetUserResult.isError && useBackendApiGetUserResult.error && !isSignedInUserUnregistered) {
      toast.error('ユーザー情報の取得に失敗しました。時間をおいてページを再読み込みしてください。', {
        description: useBackendApiGetUserResult.error.message,
        duration: 60000,
        dismissible: true,
        closeButton: true,
      })
    }
  }, [auth.isAuthenticated, useBackendApiGetUserResult.isError, useBackendApiGetUserResult.error, isSignedInUserUnregistered])

  const handleClientAction = useCallback(
    async (action: ClientActionAiChatEventPayload) => {
      console.log('Executing client action:', action)
      const { handler } = getAvailableActionsByPathname(location.pathname)
      const result = handler(action, navigate)
      if (result.isFailure) {
        console.error('Failed to execute client action:', result.error)
        return
      }

      // ClientAction実行後にClientInfoを送り直す
      if (aiChatSessionId && auth.isAuthenticated && useBackendApiGetUserResult.data) {
        console.log('Updating ClientInfo after client action execution')
        try {
          await waitForPageUpdateComplete()

          const clientInfo = createClientInfo(location.pathname)
          await updateClientInfo({
            sessionId: aiChatSessionId,
            userId: useBackendApiGetUserResult.data.id,
            clientInfo: clientInfo,
          })
          console.log('ClientInfo updated after client action execution')
        } catch (error) {
          console.error('Failed to update client info after client action:', error)
          const errorMessage = error instanceof Error ? error.message : String(error)
          toast.error('ページ情報の更新に失敗しました', {
            description: errorMessage,
          })
        }
      }
    },
    [location.pathname, navigate, aiChatSessionId, auth.isAuthenticated, useBackendApiGetUserResult.data, updateClientInfo],
  )

  const onSendUserMessageAiChatEvent = useCallback(
    async (message: string) => {
      const messageText = message || ''
      if (!messageText.trim() || isSendingMessage || !auth.isAuthenticated || !useBackendApiGetUserResult.data) {
        return
      }

      setIsSendingMessage(true)

      try {
        const clientInfo = createClientInfo(location.pathname)

        if (!aiChatSessionId) {
          // Start new session
          const response = await startAiChatSession({
            userId: useBackendApiGetUserResult.data.id,
            initialClientInfo: clientInfo,
            userMessage: messageText,
          })

          setAiChatSessionId(response.sessionId)
        } else {
          // 既存セッションへの送信前に、Subscribeが切れていないかチェック
          const isSubscriptionActive = abortControllerRef.current && !abortControllerRef.current.signal.aborted && isSubscribedRef.current

          if (!isSubscriptionActive) {
            console.log('Subscription is not active, re-subscribing before sending message...')

            // 既存のAbortControllerをクリーンアップ
            if (abortControllerRef.current) {
              abortControllerRef.current.abort()
            }

            // 再Subscribeをトリガーするために一時的にセッションIDを保存して再設定
            const sessionIdToRestore = aiChatSessionId
            setAiChatSessionId(null)

            // 次のイベントループで再設定（effectが実行されるように）
            await new Promise((resolve) => setTimeout(resolve, 0))
            setAiChatSessionId(sessionIdToRestore)

            // Subscribeが完了するまで少し待つ
            await awaitForSec(0.5)
          }

          // Send message to existing session
          await sendMessage({
            sessionId: aiChatSessionId,
            userId: useBackendApiGetUserResult.data.id,
            content: messageText,
            clientInfo: clientInfo,
          })

          // ユーザーメッセージイベントはサーバーからのイベントストリームで受信するため、手動では追加しない
        }
      } catch (error) {
        console.error('Error submitting message:', error)
        const errorMessage = error instanceof Error ? error.message : String(error)
        toast.error('メッセージの送信に失敗しました', {
          description: errorMessage,
        })
      } finally {
        setIsSendingMessage(false)
      }
    },
    [isSendingMessage, auth.isAuthenticated, useBackendApiGetUserResult.data, aiChatSessionId, startAiChatSession, sendMessage, location.pathname],
  )

  const onStartNewAiChatSession = useCallback(() => {
    // Reset session
    setAiChatSessionId(null)
    setCurrentAiChatSession(null)
    setAiChatEventIds([])
  }, [])

  // URLからaiChatSessionIdを読み取り、初回マウント時に復元
  useEffect(() => {
    if (isInitialized) return

    const urlSessionId = (searchParams as any)?.aiChatSessionId
    if (typeof urlSessionId === 'string' && urlSessionId.trim()) {
      console.log('Restoring AI chat session from URL:', urlSessionId)
      setAiChatSessionId(urlSessionId)
    }
    setIsInitialized(true)
  }, [isInitialized, searchParams])

  // aiChatSessionIdが変更されたらURLを更新
  useEffect(() => {
    if (!isInitialized) return

    const currentUrlSessionId = (searchParams as any)?.aiChatSessionId

    if (aiChatSessionId && currentUrlSessionId !== aiChatSessionId) {
      // セッションIDが存在する場合、URLに追加
      const newSearch = { ...(searchParams as any), aiChatSessionId }
      navigate({
        search: newSearch as any,
        replace: true,
      })
    } else if (!aiChatSessionId && currentUrlSessionId) {
      // セッションIDがnullの場合、URLから削除
      const { aiChatSessionId: _, ...restParams } = searchParams as any
      navigate({
        search: restParams as any,
        replace: true,
      })
    }
  }, [aiChatSessionId, isInitialized, navigate, searchParams])

  // Subscribe to session updates
  useLoggingEffect(
    () => {
      if (!aiChatSessionId || !auth.isAuthenticated || !useBackendApiGetUserResult.data) {
        // セッションIDがnullになった場合はデータをクリア
        if (!aiChatSessionId) {
          setCurrentAiChatSession(null)
          setAiChatEventIds([])
          isSubscribedRef.current = false
        }
        return
      }

      const abortController = new AbortController()
      abortControllerRef.current = abortController
      isSubscribedRef.current = false // Subscribeを開始する前はfalse

      const subscribeToSession = async () => {
        try {
          const stream = subscribeAiChatSession({
            sessionId: aiChatSessionId,
          })

          // ストリームが開始されたらtrueに設定
          isSubscribedRef.current = true
          console.log('Successfully subscribed to AI chat session')

          for await (const sessionUpdate of stream) {
            if (abortController.signal.aborted) {
              break
            }

            // セッション情報を更新
            setCurrentAiChatSession(sessionUpdate.session)
            setAiChatEventIds((prev) => {
              // 内容に変化がないか、配列の要素を順序込みで比較する
              if (prev.length === sessionUpdate.aiChatEventIds.length && prev.every((value, index) => value === sessionUpdate.aiChatEventIds[index])) {
                return prev
              }
              return sessionUpdate.aiChatEventIds
            })
          }
        } catch (error) {
          isSubscribedRef.current = false // エラー時はfalseに設定
          if (!abortController.signal.aborted) {
            console.error('Error subscribing to session:', error)
            const errorMessage = error instanceof Error ? error.message : String(error)
            toast.error('セッションの接続に失敗しました', {
              description: errorMessage,
            })
          }
        }
      }

      subscribeToSession()

      return () => {
        isSubscribedRef.current = false
        abortController.abort()
      }
    },
    [aiChatSessionId, auth.isAuthenticated, useBackendApiGetUserResult.data],
    'Subscribe to AI chat session',
  )

  // Handle client actions when events are loaded
  useEffect(() => {
    const latestAiChatEvent = aiChatEventsResult.data ? aiChatEventsResult.data[aiChatEventsResult.data.length - 1] : null
    if (
      latestAiChatEvent &&
      latestAiChatEvent.payloadJsonStatus === 'completed' &&
      latestAiChatEvent.payload.type === 'clientAction' &&
      !executedAiChatEventClientActionIds.current.has(latestAiChatEvent.id) &&
      latestAiChatEvent.payloadJsonStatus === 'completed' &&
      isCompletedClientActionAiChatEventPayload(latestAiChatEvent.payload)
    ) {
      handleClientAction(latestAiChatEvent.payload)
      executedAiChatEventClientActionIds.current.add(latestAiChatEvent.id)
    }
  }, [aiChatEventsResult.data, handleClientAction])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [])

  // Create final session object with events
  const finalAiChatSession = useMemo(() => {
    if (!currentAiChatSession || !aiChatEventsResult.data) {
      return null
    }

    return {
      ...currentAiChatSession,
      aiChatEvents: aiChatEventsResult.data,
    }
  }, [currentAiChatSession, aiChatEventsResult.data])

  return {
    currentAiChatSession: finalAiChatSession,
    onStartNewAiChatSession,
    onSendUserMessageAiChatEvent,
    isSendingMessage,
    textFieldRef,
  }
}
